import React, { FC } from 'react';
import styled from '@emotion/styled';
import { HANDY_SCALE_FACTOR } from '~Scenes/Handy/Scenes/TableSelector/TableLayout/TableMap/constants';

const StructureContainer = styled.div<{
  width: number;
  height: number;
  adjustedX: number;
  adjustedY: number;
}>`
  position: absolute;
  top: ${(props) => props.adjustedY}px;
  left: ${(props) => props.adjustedX}px;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  z-index: 1;
`;

const StructureInner = styled.div<{ rotation: number }>`
  width: 100%;
  height: 100%;
  background-color: var(--colorBlueGray1100);
  border-radius: 4px;
  position: relative;

  /**
   * Structures can be rotated at any angle. The rotation is applied from the top-left corner.
   * posX and posY represent the CENTER of the structure. We calculate adjustedX/adjustedY
   * to ensure the visual center stays at (posX, posY) even when rotated.
   */
  transform: rotate(${(props) => props.rotation}deg);
  transform-origin: top left;
`;

interface Props {
  posX: number; // Center X position
  posY: number; // Center Y position
  width: number;
  height: number;
  rotation?: number; // Rotation angle in degrees
  isHandy?: boolean; // If true, scale down for mobile devices
}

/**
 * Structure represents physical elements in the floor plan like walls, pillars, etc.
 * The structure is positioned based on its CENTER point (posX, posY), with rotation-aware
 * positioning to ensure the visual center stays consistent regardless of rotation angle.
 */
const Structure: FC<Props> = ({ posX, posY, width, height, rotation = 0, isHandy = false }) => {
  // Apply the same scaling factor as tables and markers on handy devices
  const scaledWidth = isHandy ? width * HANDY_SCALE_FACTOR : width;
  const scaledHeight = isHandy ? height * HANDY_SCALE_FACTOR : height;
  const scaledPosX = isHandy ? posX * HANDY_SCALE_FACTOR : posX;
  const scaledPosY = isHandy ? posY * HANDY_SCALE_FACTOR : posY;

  // Calculate position offset so that the structure's CENTER stays at (posX, posY)
  // Since transform-origin is 'top left', we need to adjust the position
  const rotationRad = (rotation * Math.PI) / 180;
  let cosAngle = Math.cos(rotationRad);
  let sinAngle = Math.sin(rotationRad);

  // Round to avoid floating-point errors for common angles
  // This ensures perfect alignment for 0°, 45°, 90°, etc.
  cosAngle = Math.abs(cosAngle) < 1e-10 ? 0 : cosAngle;
  sinAngle = Math.abs(sinAngle) < 1e-10 ? 0 : sinAngle;
  if (Math.abs(Math.abs(cosAngle) - 1) < 1e-10) {
    cosAngle = Math.sign(cosAngle);
  }
  if (Math.abs(Math.abs(sinAngle) - 1) < 1e-10) {
    sinAngle = Math.sign(sinAngle);
  }

  // Center point in local coordinates (before rotation)
  const centerLocalX = scaledWidth / 2;
  const centerLocalY = scaledHeight / 2;

  // Where the center ends up after rotation around top-left
  const centerAfterRotation = {
    x: centerLocalX * cosAngle - centerLocalY * sinAngle,
    y: centerLocalX * sinAngle + centerLocalY * cosAngle,
  };

  // Adjust position so visual center is at (posX, posY)
  const adjustedX = Math.round(scaledPosX - centerAfterRotation.x);
  const adjustedY = Math.round(scaledPosY - centerAfterRotation.y);

  return (
    <StructureContainer
      width={scaledWidth}
      height={scaledHeight}
      adjustedX={adjustedX}
      adjustedY={adjustedY}
    >
      <StructureInner rotation={rotation} />
    </StructureContainer>
  );
};

export default Structure;
